import type { LateralEdgePlacement } from "building-types/entities/edgePlacementTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { classifyContainmentVec2PolyLine2, Vec2PolyLine2ContainmentClass } from "geom/containment";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { ray2PolyLine2IntersectionMakeUnique, intersectRay2PolyLine2Filtered } from "geom/intersect";
import type { Ray2PolyLine2Intersection } from "geom/intersectRay2";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { poleOfInaccessibilityPolygon } from "geom/label";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2ByTwoPoints, polyLine2EdgeEval, polyLine2EdgeNormal } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { vec2Dist, vec2Dot, vec2Sub } from "geom/vec2";

enum RoomOutlineIntersectionType {
  PARAM = "PARAM",
  MID_PT = "MID_PT",
  POLE_OF_INACCESSIBILITY = "POLE_OF_INACCESSIBILITY",
}

interface RoomOutlineIntersections {
  readonly int1: Vec2;
  readonly int2: Vec2;
}

interface RoomOutlineIntersectionInfo extends RoomOutlineIntersections {
  readonly roomEdgePos: Vec2;
  readonly type: RoomOutlineIntersectionType;
}

function roomOutlineIntersectionsByEdgeParam(
  param: number,
  edgeIndex: number,
  roomOutline: PolyLine2,
  wallLayerOutline: PolyLine2 | undefined,
): RoomOutlineIntersectionInfo | null {
  const [roomOutlineX, roomOutlineY] = polyLine2EdgeEval(roomOutline, edgeIndex, param, false);
  const [nx, ny] = polyLine2EdgeNormal(roomOutline, edgeIndex, true);

  if (!wallLayerOutline) {
    return null;
  }

  const ints = intersectRoomOutlineAtPt(roomOutlineX, roomOutlineY, nx, ny, wallLayerOutline);

  if (!ints) {
    return null;
  }

  return {
    roomEdgePos: [roomOutlineX, roomOutlineY],
    int1: ints.int1,
    int2: ints.int2,
    type: RoomOutlineIntersectionType.PARAM,
  };
}

function roomOutlineIntersectionsByEdgeMidPt(
  param: number,
  edgeIndex: number,
  roomOutline: PolyLine2,
  wallLayerOutline: PolyLine2,
): RoomOutlineIntersectionInfo | null {
  const [roomOutlineX, roomOutlineY] = polyLine2EdgeEval(roomOutline, edgeIndex, param, false);
  const [edgeMidX, edgeMidY] = polyLine2EdgeEval(roomOutline, edgeIndex, 0.5, true);
  const [nx, ny] = polyLine2EdgeNormal(roomOutline, edgeIndex, true);
  const ints = intersectRoomOutlineAtPt(edgeMidX, edgeMidY, nx, ny, wallLayerOutline);

  if (!ints) {
    return null;
  }

  const {
    int1: [int1x, int1y],
    int2: [int2x, int2y],
  } = ints;

  // create new intersection points at the provided param
  const [int1MoveX, int1MoveY] = vec2Sub(int1x, int1y, edgeMidX, edgeMidY);
  const [int2MoveX, int2MoveY] = vec2Sub(int2x, int2y, edgeMidX, edgeMidY);

  const int1xAtParam = roomOutlineX + int1MoveX;
  const int1yAtParam = roomOutlineY + int1MoveY;
  const int2xAtParam = roomOutlineX + int2MoveX;
  const int2yAtParam = roomOutlineY + int2MoveY;

  return {
    roomEdgePos: [roomOutlineX, roomOutlineY],
    int1: [int1xAtParam, int1yAtParam],
    int2: [int2xAtParam, int2yAtParam],
    type: RoomOutlineIntersectionType.MID_PT,
  };
}

function roomOutlineIntersectionsByPoleOfInaccessibility(
  param: number,
  edgeIndex: number,
  roomOutline: PolyLine2,
): RoomOutlineIntersectionInfo {
  const [roomOutlineX, roomOutlineY] = polyLine2EdgeEval(roomOutline, edgeIndex, param, false);
  const [nx, ny] = polyLine2EdgeNormal(roomOutline, edgeIndex, true);
  const centroid = poleOfInaccessibilityPolygon([roomOutline]);
  if (!centroid) {
    return {
      roomEdgePos: [roomOutlineX, roomOutlineY],
      int1: [roomOutlineX, roomOutlineY],
      int2: [roomOutlineX, roomOutlineY],
      type: RoomOutlineIntersectionType.POLE_OF_INACCESSIBILITY,
    };
  }
  const [xCentroid, yCentroid] = centroid;

  const ints = intersectRay2PolyLine2(roomOutline, xCentroid, yCentroid, nx, ny);
  ray2PolyLine2IntersectionMakeUnique(ints);
  if (ints.length < 2) {
    return {
      roomEdgePos: [roomOutlineX, roomOutlineY],
      int1: [roomOutlineX, roomOutlineY],
      int2: [roomOutlineX, roomOutlineY],
      type: RoomOutlineIntersectionType.POLE_OF_INACCESSIBILITY,
    };
  }

  const positiveInts: Ray2PolyLine2Intersection[] = [];
  const negativeInts: Ray2PolyLine2Intersection[] = [];
  ints.forEach((x) => {
    if (x.t < 0) {
      negativeInts.push(x);
    } else {
      positiveInts.push(x);
    }
  });
  const { x: int1x, y: int1y } = negativeInts[negativeInts.length - 1];
  const { x: int2x, y: int2y } = positiveInts[0];

  const dist = vec2Dist(int1x, int1y, int2x, int2y);

  return {
    roomEdgePos: [roomOutlineX, roomOutlineY],
    int1: [roomOutlineX, roomOutlineY],
    int2: [roomOutlineX + nx * dist, roomOutlineY + ny * dist],
    type: RoomOutlineIntersectionType.POLE_OF_INACCESSIBILITY,
  };
}

function intersectRoomOutlineAtPt(
  roomOutlineX: number,
  roomOutlineY: number,
  nx: number,
  ny: number,
  wallLayerOutline: PolyLine2,
): RoomOutlineIntersections | null {
  let x: number = roomOutlineX;
  let y: number = roomOutlineY;

  const outlineIntersections = intersectRay2PolyLine2(wallLayerOutline, x, y, nx, ny, DIST_EPSILON);
  ray2PolyLine2IntersectionMakeUnique(outlineIntersections);
  //need to know if the shooting point is inside the outline. this is important because we want to know the starting point of the segment
  //from which the component location is computed
  const cont = classifyContainmentVec2PolyLine2(x, y, wallLayerOutline);

  let distNeg = Number.MAX_VALUE;
  let distPos = Number.MAX_VALUE;
  let shootsInFromOutsideOrOnEdge = false;
  for (let i = 0; i < outlineIntersections.length; i++) {
    const { x: intx, y: inty, t } = outlineIntersections[i];
    const dist = vec2Dist(roomOutlineX, roomOutlineY, intx, inty);

    if (dist < DIST_EPSILON) {
      x = intx;
      y = inty;
      shootsInFromOutsideOrOnEdge = true;
      break;
    }

    if (cont === Vec2PolyLine2ContainmentClass.INSIDE) {
      if (t < 0 && Math.abs(t) < distNeg) {
        x = intx;
        y = inty;
        distNeg = Math.abs(t);
      }
    } else {
      if (t > 0 && t < distPos) {
        x = intx;
        y = inty;
        distPos = t;
        shootsInFromOutsideOrOnEdge = true;
      }
    }
  }

  if (cont === Vec2PolyLine2ContainmentClass.INSIDE || shootsInFromOutsideOrOnEdge) {
    // ray intersects the wall layer outline (inclusive of ray being coincident with the outline)
    // use contracted polygon to make sure we get at least two intersections when the shooting point is on the edge itself
    const contractedWallLayerOutline = offsetPolyLine2(wallLayerOutline, -DIST_EPSILON_SQUARED);
    const ints = intersectRay2PolyLine2Filtered(contractedWallLayerOutline, x, y, nx, ny, DIST_EPSILON);
    ray2PolyLine2IntersectionMakeUnique(ints);

    // this should never happen but just in case
    if (ints.length < 2) {
      return {
        int1: [x, y],
        int2: [x, y],
      };
    }
    const { x: int1x, y: int1y } = ints[0];
    const { x: int2x, y: int2y } = ints[1];

    return {
      int1: [int1x, int1y],
      int2: [int2x, int2y],
    };
  }

  return null;
}

function roomOutlineIntersectionsWithFallbacks(
  param: number,
  edgeIndex: number,
  room: { outline: PolyLine2 },
  wallLayerOutline: PolyLine2,
  datumOffset: number,
): RoomOutlineIntersectionInfo | null {
  const { outline } = room;
  const ints =
    roomOutlineIntersectionsByEdgeParam(param, edgeIndex, outline, wallLayerOutline) ??
    roomOutlineIntersectionsByEdgeMidPt(param, edgeIndex, outline, wallLayerOutline) ??
    roomOutlineIntersectionsByPoleOfInaccessibility(param, edgeIndex, outline);

  if (!ints) {
    return null;
  }

  const {
    roomEdgePos: [rex, rey],
    int1: [int1x, int1y],
    type,
  } = ints;

  const distToFirstInt = vec2Dist(rex, rey, int1x, int1y);
  let int1xAdjusted = int1x;
  let int1yAdjusted = int1y;

  // case where the param is off the room edge, but room outline intersections were found
  // This handles cases where the room outline is not rectangular (room is L shaped).
  if (type === RoomOutlineIntersectionType.PARAM && distToFirstInt - datumOffset > DIST_EPSILON) {
    const [nx, ny] = polyLine2EdgeNormal(room.outline, edgeIndex, true);
    int1xAdjusted = rex + nx * datumOffset;
    int1yAdjusted = rey + ny * datumOffset;
  }

  return {
    ...ints,
    int1: [int1xAdjusted, int1yAdjusted],
  };
}

function roomOutlineIntersectionsWithFallbacksDeprecated(
  param: number,
  edgeIndex: number,
  room: Room,
  wallLayerOutline: PolyLine2 | undefined,
): RoomOutlineIntersectionInfo | null {
  const { outline } = room;
  return (
    roomOutlineIntersectionsByEdgeParam(param, edgeIndex, outline, wallLayerOutline) ??
    roomOutlineIntersectionsByPoleOfInaccessibility(param, edgeIndex, outline)
  );
}

function offsetFromRoomOutlineIntersections(
  intersectionInfo: RoomOutlineIntersectionInfo,
  placement: Pick<LateralEdgePlacement, "lateralOffset" | "lateralOffsetNormalized">,
  room: Room,
  edgeIndex: number,
): number {
  const {
    int1,
    int2,
    roomEdgePos: [rx, ry],
  } = intersectionInfo;

  const [int1x, int1y] = int1;
  const [int2x, int2y] = int2;
  const [ox, oy] = polyLine2EdgeEval(
    polyLine2ByTwoPoints(int1x, int1y, int2x, int2y),
    0,
    placement.lateralOffset,
    placement.lateralOffsetNormalized,
  );

  const absDist = vec2Dist(rx, ry, ox, oy);
  const [rnx, rny] = polyLine2EdgeNormal(room.outline, edgeIndex);
  const [onx, ony] = [ox - rx, oy - ry];
  const dist = vec2Dot(onx, ony, rnx, rny) > 0 ? -absDist : absDist;
  return dist;
}

export function roomEdgeOffsetFromOutline(
  room: Room,
  edgeIndex: number,
  param: number,
  wallLayerOutline: PolyLine2,
  placement: Pick<LateralEdgePlacement, "lateralOffset" | "lateralOffsetNormalized">,
  datumOffset: number,
): number {
  const ints = roomOutlineIntersectionsWithFallbacks(param, edgeIndex, room, wallLayerOutline, datumOffset);
  if (!ints) {
    return 0;
  }

  return offsetFromRoomOutlineIntersections(ints, placement, room, edgeIndex);
}

/**
 * @deprecated
 * Specifically for use with deprecated electrical placements.
 */
export function roomEdgeOffsetFromOutlineDeprecated(
  room: Room,
  edgeIndex: number,
  resolvedParam: number,
  wallLayerOutline: PolyLine2 | undefined,
  placement: Pick<LateralEdgePlacement, "lateralOffset" | "lateralOffsetNormalized">,
): number {
  const ints = roomOutlineIntersectionsWithFallbacksDeprecated(resolvedParam, edgeIndex, room, wallLayerOutline);
  if (!ints) {
    return 0;
  }

  return offsetFromRoomOutlineIntersections(ints, placement, room, edgeIndex);
}

export function roomWidthAtParamFromOutline(
  param: number,
  edgeIndex: number,
  room: { outline: PolyLine2 },
  wallLayerOutline: PolyLine2,
  datumOffset: number,
): number {
  const ints = roomOutlineIntersectionsWithFallbacks(param, edgeIndex, room, wallLayerOutline, datumOffset);
  if (!ints) {
    return 0;
  }

  const {
    int1: [int1x, int1y],
    int2: [int2x, int2y],
  } = ints;

  return vec2Dist(int1x, int1y, int2x, int2y);
}
